import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
} from "@mui/material";
import React from "react";
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import { GridColDef } from '@mui/x-data-grid';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import IconButton from "@mui/material/IconButton";
import axios from "axios";
import { alpha, styled } from '@mui/material/styles';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { IApplication } from "../interfaces/IApplication";
import { setPageTitle } from "../slices/applicationSlice";
import { useAppDispatch } from "../hooks";
import SearchChips from "../components/SearchChips";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SupportIcon from '@mui/icons-material/ContactSupport';
import { ISearchChips } from "../interfaces/Core/ISearchChips";
import ActionButton from "../components/ActionButton";
import ActionMenu from "../components/ActionMenu";

type Anchor = 'top' | 'left' | 'bottom' | 'right';

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGrid)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[300],
    '&:hover, &.Mui-hovered': {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      '@media (hover: none)': {
        backgroundColor: 'transparent',
      },
    },
    '&.Mui-selected': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity,
      ),
      '&:hover, &.Mui-hovered': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity,
        ),
        // Reset on touch devices, it doesn't add specificity
        '@media (hover: none)': {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity,
          ),
        },
      },
    },
  },
}));

export default function PatchingShortagesExtras() {

  function DownloadClicked(_event: React.MouseEvent<HTMLElement>): void {
    
    setOpen(true);
  }

  const [open, setOpen] = useState(false);

  function handleClose() {
    setOpen(!open);
  }

  const buttons = [
    <ActionButton action={DownloadClicked} buttonLabel="Download report" />,
    
  ];

  const dispatch = useAppDispatch();

  useEffect(() => {
    let appState: IApplication = {
      pageTitle: "Patching Shortages/Extras",
      backgroundPath: "/assets/images/patchingbg.jpg"
    }
    dispatch(setPageTitle(appState));
  }, []);

  const [state, setState] = React.useState({
    top: false,
    left: false,
    bottom: false,
    right: false,
  });

  interface IShortageExtraRow {
    species: string,
    variety: string,
    color: string,
    expectedCount: number,
    buffer: number,
    shortageExtra: number,
    crew: string,
    startTime: Date,
    endTime: Date
  }

  const [rows, setRows] = useState<IShortageExtraRow[]>([]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_ENDPOINT}/api/PatchingProductions`)
      .then(res => {
        console.log("RESPONSE", res.data);
        setRows(res.data);
      })
  }, []);

  const columns: GridColDef[] = [
    //{ field: 'id', headerName: 'ID', width: 130 },
    { field: 'species', headerName: 'Species', width: 130 },
    { field: 'variety', headerName: 'Variety', width: 130 },
    { field: 'color', headerName: 'Color', width: 130, },
    { field: 'expectedCount', headerName: 'Expected Count', width: 120, },
    { field: 'buffer', headerName: 'Buffer', width: 120, },
    { field: 'type', headerName: 'Shortage / Extra', width: 160, },
    { field: 'crew', headerName: 'Crew', width: 150, },
    { field: 'startTime', headerName: 'Start Time', width: 90, },
    { field: 'endTime', headerName: 'End Time', width: 110, },
  ];

  const Item = styled(Paper)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' ? '#010101' : '#fff',
    ...theme.typography.body2,
    padding: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    border: 0,
    borderRadius: 0,
    boxShadow: "none",
  }));

  function valuetext(value: number) {
    return `${value}°C`;
  }

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }));

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#89B219',
      },
    },
  });

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const handleDelete = () => {
    console.info('You clicked the delete icon.');
  };
  
  const [helpOpen, setHelpOpen] = React.useState(false);

  const handleClickHelpOpen = () => {
    setHelpOpen(true);
  };

  const handleHelpClose = () => {
    setHelpOpen(false);
  };

  const [searchInformation, setSearchInformation] = React.useState<ISearchChips>();

  const handleSearchChips = (searchInformation: ISearchChips) => {
    setSearchInformation(searchInformation);
  }

  return (
    <div>
     <ActionMenu buttons={buttons} />
      <Grid container spacing={0} bgcolor="#101010" >
        <ThemeProvider theme={darkTheme}>
          <SearchChips handleSearch={handleSearchChips}></SearchChips>
        </ThemeProvider>
        <Grid item xs={12}>
          <Item>
            <div style={{ height: 'calc(100vh - 156px)', width: '100%' }}>
              <StripedDataGrid
                rows={rows}
                columns={columns}
                pageSize={20}
                rowsPerPageOptions={[20]}
                checkboxSelection
                sx={{ borderColor: 'rgba(100,100,100,0.2)' }}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                }
              />
            </div>
          </Item>
        </Grid>
      </Grid>
    </div>

  );
}